import {useFrame}                                                                           from '@react-three/fiber'
import {useMemo}                                                                            from 'react'
import {cos, distance, float, Fn, mix, sin, smoothstep, sub, uniform, uv, vec2, vec3, vec4} from 'three/tsl'

export function useGradientMaterial({hovering}) {
	// Create uniform and shader calculations
	const uTime = useMemo(() => uniform(float(0.0)), [])

	// Get UV coordinates and time
	const vUv          = uv()
	const timeProgress = float(float(-1.).mul(cos(uTime)).mul(.5).add(.5)).toVar('timeProgress')

	//region Functions
	const colorVisualization = Fn(([value_immutable]) => {

		const value = float(value_immutable).toVar()

		return vec4(clamp(value, .0, 1.), .0, clamp(value, float(-1.), 0.).sub(1.), 1.)

	}).setLayout({
		             name:   'colorVisualization',
		             type:   'vec4',
		             inputs: [
			             {
				             name: 'value',
				             type: 'float',
			             },
		             ],
	             })

	const rgbToVec3 = Fn(([color_immutable]) => {

		const color = vec3(color_immutable).toVar()

		return vec3(color.r.div(255.), color.g.div(255.), color.b.div(255.))

	})
	//endregion Functions

	const colorNode = Fn(() => {
		const newUV                = vec2(vUv).toVar('newUv')
		const circleRadius         = float(.001).toVar('circleRadius')
		const circleRadiusBlur     = float(circleRadius.add(.5)).toVar('circleRadiusBlur')
		const circlePosition       = vec2(sin(uTime.negate()).mul(.5).add(.5), cos(uTime.negate()).mul(.5).add(.5)).toVar('circlePosition')
		const distanceToCircle     = float(distance(newUV, circlePosition)).toVar('distanceToCircle')
		const circle               = float(sub(1., smoothstep(circleRadius, circleRadiusBlur, distanceToCircle))).toVar('circle')
		const circlePosition2      = vec2(sin(uTime.negate().add(float(Math.PI).mul(.5))).mul(.5).add(.5), cos(uTime.negate().add(float(Math.PI).mul(.5))).mul(.5).add(.5)).toVar()
		const distanceToCircle2    = float(distance(newUV, circlePosition2)).toVar()
		const circle2              = float(sub(1., smoothstep(circleRadius, circleRadiusBlur, distanceToCircle2))).toVar()
		const circlePosition3      = vec2(sin(uTime.negate().add(Math.PI)).mul(.5).add(.5), cos(uTime.negate().add(Math.PI)).mul(.5).add(.5)).toVar()
		const distanceToCircle3    = float(distance(newUV, circlePosition3)).toVar()
		const circle3              = float(sub(1., smoothstep(circleRadius, circleRadiusBlur, distanceToCircle3))).toVar()
		const circlePosition4      = vec2(sin(uTime.negate().add(float(Math.PI).mul(1.5))).mul(.5).add(.5), cos(uTime.negate().add(float(Math.PI).mul(1.5))).mul(.5).add(.5)).toVar()
		const distanceToCircle4    = float(distance(newUV, circlePosition4)).toVar()
		const circle4              = float(sub(1., smoothstep(circleRadius, circleRadiusBlur, distanceToCircle4))).toVar()
		const backgroundColorStart = vec4(mix(rgbToVec3(vec3(122., 184., 234.)), rgbToVec3(vec3(43., 117., 237.)), vUv.x), 1.).toVar()
		const backgroundColorEnd   = vec4(mix(rgbToVec3(vec3(249., 95., 194.)), rgbToVec3(vec3(242., 101., 78.)), vUv.x), 1.).toVar()
		const backgroundColor      = vec4(mix(backgroundColorStart, backgroundColorEnd, timeProgress)).toVar()
		const circle1ColorStart    = vec4(rgbToVec3(vec3(122., 184., 234.)), 1.).toVar()
		const circle1ColorEnd      = vec4(rgbToVec3(vec3(242., 101., 78.)), 1.).toVar()
		const circle2ColorStart    = vec4(1., 1., 1., 1.).toVar()
		const circle2ColorEnd      = vec4(.0, .0, .0, 1.).toVar()
		const circle3ColorStart    = vec4(rgbToVec3(vec3(43., 117., 237.)), 1.).toVar()
		const circle3ColorEnd      = vec4(rgbToVec3(vec3(249., 95., 194.)), 1.).toVar()
		const circle4ColorStart    = vec4(rgbToVec3(vec3(11., 25., 62.)), 1.).toVar()
		const circle4ColorEnd      = vec4(rgbToVec3(vec3(121., 49., 108.)), 1.).toVar()
		const circle1Color         = vec4(mix(circle1ColorStart, circle1ColorEnd, timeProgress)).toVar()
		const circle2Color         = vec4(mix(circle2ColorStart, circle2ColorEnd, timeProgress)).toVar()
		const circle3Color         = vec4(mix(circle3ColorStart, circle3ColorEnd, timeProgress)).toVar()
		const circle4Color         = vec4(mix(circle4ColorStart, circle4ColorEnd, timeProgress)).toVar()

		const finalColor = backgroundColor
		finalColor.assign(mix(finalColor, circle1Color, circle))
		finalColor.assign(mix(finalColor, circle2Color, circle2))
		finalColor.assign(mix(finalColor, circle3Color, circle3))
		finalColor.assign(mix(finalColor, circle4Color, circle4))

		return finalColor
	})()

	// Update time in animation frame
	useFrame((state, delta) => {
		const timeMultiplier = hovering ? 1 : .1
		uTime.value += delta * timeMultiplier * 4
	})

	return {
		key:       uTime.id,
		colorNode: colorNode,
	}
}
